
import { useQuery } from '@tanstack/react-query';
import { pb, Lesson } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';

export const useLessons = (tutorUserIdOverride?: string) => {
  const { user } = useAuth();
  
  const query = useQuery({
    queryKey: ['lessons', user?.id, user?.role, tutorUserIdOverride],
    queryFn: async ({ signal }) => {
      // If we have a specific tutor user ID override, only fetch their lesson library
      if (tutorUserIdOverride) {
        
        if (signal?.aborted) {
          return [];
        }
        
        try {
          const lessons = await pb.collection('lesson_library').getList(1, 50, {
            filter: `tutorId = "${tutorUserIdOverride}"`,
            sort: '-created',
            requestKey: null
          });
          
          if (signal?.aborted) {
            return [];
          }
          
          // Fetch tutor data for the lessons
          let tutorData = null;
          if (lessons.items.length > 0) {
            try {
              // First try to get tutor profile by user ID
              const tutorProfiles = await pb.collection('tutors').getList(1, 1, {
                filter: `user = "${tutorUserIdOverride}"`,
                expand: 'user'
              });
              
              if (tutorProfiles.items.length > 0) {
                tutorData = {
                  user: tutorProfiles.items[0].expand?.user,
                  profile: tutorProfiles.items[0]
                };
                console.log('useLessons: Found tutor data for lessons:', tutorData);
              } else {
                // Fallback: try to get user data directly
                const userData = await pb.collection('users').getOne(tutorUserIdOverride);
                tutorData = {
                  user: userData,
                  profile: null
                };
                console.log('useLessons: Found user data for lessons:', tutorData);
              }
            } catch (tutorError) {
              console.error('useLessons: Error fetching tutor data:', tutorError);
            }
          }
          
          // Transform library lessons and attach tutor data
          const transformedLessons = lessons.items.map(lesson => ({
            ...lesson,
            type: 'lesson',
            collectionName: 'lesson_library',
            status: lesson.status || 'pending',
            tutorData: tutorData // Attach tutor data to each lesson
          }));
          
          return transformedLessons;
        } catch (error: any) {
          if (error?.message?.includes('autocancelled') || error?.name === 'AbortError' || signal?.aborted) {
            return [];
          }
          console.error('useLessons: Error fetching tutor-specific lessons:', error);
          return [];
        }
      }

      if (!user?.id) {
        return [];
      }
      
      try {
        
        if (user.role === 'tutor') {
          
          if (signal?.aborted) {
            return [];
          }
          
          // For current tutor viewing their own lessons, fetch from lesson_library
          const libraryLessons = await pb.collection('lesson_library').getList(1, 50, {
            filter: `tutorId = "${user.id}"`,
            sort: '-created',
            requestKey: null
          });
          
          if (signal?.aborted) {
            return [];
          }
          
          
          
          // Also fetch bookings where this tutor is a student (booked lessons with other tutors)
          let studentBookings = [];
          try {
            if (!signal?.aborted) {
              const bookingsResult = await pb.collection('bookings').getList(1, 50, {
                filter: `studentId="${user.id}"`,
                expand: 'tutorId',
                sort: '-date',
              });
              
              if (!signal?.aborted) {
                studentBookings = bookingsResult.items;
              }
            }
          } catch (bookingError: any) {
            if (!bookingError?.message?.includes('autocancelled') && bookingError?.name !== 'AbortError') {
              console.error('useLessons: Error fetching student bookings for tutor:', bookingError);
            }
          }
          
          // Transform library lessons
          const transformedLibraryLessons = libraryLessons.items.map(lesson => {
            return {
              ...lesson,
              type: 'lesson',
              collectionName: 'lesson_library',
              status: lesson.status || 'pending'
            };
          });
          
          // Transform student bookings into lesson-like objects
          const transformedBookings = studentBookings.map(booking => {
            
            return {
              ...booking,
              title: `Session: ${booking.subject}`,
              description: booking.notes || `Tutoring session for ${booking.subject}`,
              date: booking.date,
              created: booking.date,
              status: booking.status || 'pending',
              type: 'student-booking',
              collectionName: 'bookings'
            };
          });
          
          const allLessons = [...transformedLibraryLessons, ...transformedBookings];
          
          return allLessons;
        } else {
          // For students, get lessons from bookings where they are the student
          
          if (signal?.aborted) {
            return [];
          }
          
          const studentBookings = await pb.collection('bookings').getList(1, 50, {
            filter: `studentId="${user.id}"`,
            expand: 'tutorId',
            sort: '-date',
          });
          
          if (signal?.aborted) {
            return [];
          }
          
          
          
          // Transform bookings into lesson-like objects for the student
          const lessons = studentBookings.items.map(booking => {
            
            return {
              ...booking,
              title: `Session: ${booking.subject}`,
              description: booking.notes || `Tutoring session for ${booking.subject}`,
              date: booking.date,
              created: booking.date,
              status: booking.status || 'pending',
              type: 'student-booking',
              collectionName: 'bookings'
            };
          });
          
          
          return lessons;
        }
      } catch (error: any) {
        // Handle autocancellation errors gracefully
        if (error?.message?.includes('autocancelled') || error?.name === 'AbortError' || signal?.aborted) {
          
          return [];
        }
        
        console.error('useLessons: Detailed error fetching lessons:', {
          message: error.message,
          status: error.status,
          data: error.data,
          originalError: error
        });
        return [];
      }
    },
    enabled: !!(user?.id || tutorUserIdOverride), // Allow query when tutorUserIdOverride is provided even without user
    refetchOnWindowFocus: false,
    staleTime: 30000, // Increased from 5 seconds to 30 seconds
    gcTime: 300000, // Increased from 60 seconds to 5 minutes
    retry: (failureCount, error) => {
      // Don't retry autocancelled requests
      if (error?.message?.includes('autocancelled') || error?.name === 'AbortError') {
        return false;
      }
      return failureCount < 2;
    },
  });

  

  return {
    lessons: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refreshLessons: query.refetch,
  };
};

// New hook specifically for fetching lessons by subject
export const useLessonsBySubject = (subject: string) => {
  const query = useQuery({
    queryKey: ['lessons-by-subject', subject],
    queryFn: async ({ signal }) => {
      if (!subject) return [];
      
      
      
      try {
        // Create multiple filter variations to catch lessons
        const subjectFilters = [
          `subject ~ "${subject}"`,
          `subject = "${subject}"`,
          `subject ~ "${subject.toLowerCase()}"`,
          `subject ~ "${subject.toUpperCase()}"`
        ];
        
        let allLessons = [];
        
        for (const filter of subjectFilters) {
          try {
            if (signal?.aborted) break;
            
            
            const lessons = await pb.collection('lesson_library').getList(1, 50, {
              filter,
              sort: '-created',
              requestKey: null
            });
            
            
            allLessons = [...allLessons, ...lessons.items];
          } catch (filterError) {
            
          }
        }
        
        // Remove duplicates based on lesson ID
        const uniqueLessons = allLessons.filter((lesson, index, self) => 
          index === self.findIndex(l => l.id === lesson.id)
        );
        
        
        
        // Transform lessons and add tutor data
        const lessonsWithTutorData = await Promise.all(
          uniqueLessons.map(async (lesson) => {
            try {
              if (signal?.aborted) return { ...lesson, tutorData: null };
              
              // Get tutor profile and user data
              const [tutorProfile, userData] = await Promise.all([
                pb.collection('tutors').getFirstListItem(`user = "${lesson.tutorId}"`, { requestKey: null }).catch(() => null),
                pb.collection('users').getOne(lesson.tutorId, { requestKey: null }).catch(() => null)
              ]);
              
              return {
                ...lesson,
                tutorData: {
                  profile: tutorProfile,
                  user: userData
                }
              };
            } catch (error) {
              
              return {
                ...lesson,
                tutorData: null
              };
            }
          })
        );
        
        return lessonsWithTutorData;
      } catch (error: any) {
        if (error?.message?.includes('autocancelled') || error?.name === 'AbortError' || signal?.aborted) {
          
          return [];
        }
        
        
        return [];
      }
    },
    enabled: !!subject,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    gcTime: 300000,
    retry: (failureCount, error) => {
      if (error?.message?.includes('autocancelled') || error?.name === 'AbortError') {
        return false;
      }
      return failureCount < 2;
    },
  });

  return {
    lessons: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refreshLessons: query.refetch,
  };
};
